import { Alert, Button, Card, Descriptions, Space, Table } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Home = {
  workspace: {
    as_of: string;
    staff_link: { first_name?: string; last_name?: string; employee_code?: string } | null;
    session: { name?: string; code?: string } | null;
    periods: Array<{ period_code?: string; subject_name?: string; section_name?: string }>;
    covers: unknown[];
    attendance: unknown[];
  };
  links: Record<string, string>;
};

export default function TeacherPortalPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['education-portal-teacher'],
    queryFn: async () => (await api.get<Home>('/education/portal/teacher')).data,
  });
  const home = query.data;
  const day = home?.workspace;

  return (
    <EducationPageChrome
      title={t('teacherPortal')}
      description={t('teacherPortalIntro')}
      extra={
        <Button type="primary" onClick={() => navigate('/education/workspace')}>
          {t('teacherWorkspace')}
        </Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('teacherPortalComposeHint')} />
        <Card size="small" loading={query.isLoading}>
          {day ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('date')}>{day.as_of}</Descriptions.Item>
              <Descriptions.Item label={t('session')}>
                {day.session?.name || day.session?.code || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('staff')}>
                {day.staff_link
                  ? [day.staff_link.first_name, day.staff_link.last_name, day.staff_link.employee_code]
                      .filter(Boolean)
                      .join(' ')
                  : t('noStaffLink')}
              </Descriptions.Item>
              <Descriptions.Item label={t('periods')}>{day.periods?.length ?? 0}</Descriptions.Item>
            </Descriptions>
          ) : null}
        </Card>
        <Card size="small" title={t('todayPeriods')}>
          <Table
            rowKey={(_, i) => String(i)}
            size="small"
            pagination={false}
            dataSource={day?.periods ?? []}
            columns={[
              { title: t('period'), dataIndex: 'period_code', width: 100 },
              { title: t('subject'), dataIndex: 'subject_name' },
              { title: t('section'), dataIndex: 'section_name' },
            ]}
          />
        </Card>
        <Card size="small" title={t('quickLinks')}>
          <Space wrap>
            {Object.entries(home?.links || {}).map(([key, path]) => (
              <Button key={key} onClick={() => navigate(path)}>
                {key}
              </Button>
            ))}
          </Space>
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
